import React, { useEffect, useState } from 'react';
import { FiCheck, FiUsers, FiX } from 'react-icons/fi';
import './share_access_modal.css';

function authHeaders(json = false) {
  const token = localStorage.getItem('walmates-token');
  return {
    Authorization: `Bearer ${token}`,
    ...(json ? { 'Content-Type': 'application/json' } : {}),
  };
}

async function jsonResponse(response) {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `Request failed (${response.status})`);
  return body;
}

export default function ShareAccessModal({ resource, itemId, title, onClose, onSaved, endpoint, idParam }) {
  const [users, setUsers] = useState([]);
  const [all, setAll] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const idKey = idParam || (resource === 'vault' ? 'vault_id' : 'todo_id');
  const shareEndpoint = endpoint || `/api/${resource}/share`;

  useEffect(() => {
    let active = true;
    const userId = localStorage.getItem('walmates-user_id');
    Promise.all([
      fetch('/api/users', { headers: authHeaders() }).then(jsonResponse),
      fetch(`${shareEndpoint}?${new URLSearchParams({ user_id: userId, [idKey]: itemId })}`, {
        headers: authHeaders(),
      }).then(jsonResponse),
    ])
      .then(([people, settings]) => {
        if (!active) return;
        setUsers(people || []);
        setAll(!!settings.shared_with_all);
        setSelected(new Set((settings.user_ids || []).map(Number)));
      })
      .catch((err) => active && setError(err.message))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [resource, itemId, idKey, shareEndpoint]);

  const toggleUser = (id) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const body = {
        user_id: localStorage.getItem('walmates-user_id'),
        [idKey]: itemId,
        shared_with_all: all,
        user_ids: all ? [] : [...selected],
      };
      const result = await fetch(shareEndpoint, {
        method: 'PUT',
        headers: authHeaders(true),
        body: JSON.stringify(body),
      }).then(jsonResponse);
      onSaved?.(result);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="share-modal-backdrop" role="presentation" onClick={onClose}>
      <section className="share-modal" role="dialog" aria-modal="true" aria-label={`Share ${title}`} onClick={(event) => event.stopPropagation()}>
        <header className="share-modal-header">
          <div className="share-modal-heading">
            <span className="share-modal-icon"><FiUsers /></span>
            <div><h2>Share</h2><p>{title}</p></div>
          </div>
          <button type="button" className="share-modal-close" onClick={onClose} aria-label="Close"><FiX /></button>
        </header>

        {loading ? <div className="share-modal-state">Loading people…</div> : (
          <div className="share-modal-options">
            <button type="button" className={`share-person${all ? ' is-selected' : ''}`} onClick={() => setAll((value) => !value)}>
              <span className="share-person-avatar">All</span>
              <span className="share-person-name">Everyone</span>
              <span className="share-person-check">{all && <FiCheck />}</span>
            </button>
            {!all && users.map((user) => {
              const checked = selected.has(Number(user.id));
              return (
                <button key={user.id} type="button" className={`share-person${checked ? ' is-selected' : ''}`} onClick={() => toggleUser(Number(user.id))}>
                  <span className="share-person-avatar">{String(user.login_id || '?').slice(0, 2).toUpperCase()}</span>
                  <span className="share-person-name">{user.login_id}</span>
                  <span className="share-person-check">{checked && <FiCheck />}</span>
                </button>
              );
            })}
          </div>
        )}

        {error && <div className="share-modal-error">{error}</div>}
        <footer className="share-modal-footer">
          <button type="button" className="share-cancel" onClick={onClose}>Cancel</button>
          <button type="button" className="share-save" onClick={save} disabled={loading || saving}>{saving ? 'Sharing…' : 'Share'}</button>
        </footer>
      </section>
    </div>
  );
}
